import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import { isValid, parseISO } from 'date-fns';
import React from 'react';
import { DateRangePicker } from '../DateRangePicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormDateRangePicker = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const data = (_a = props.getData()) !== null && _a !== void 0 ? _a : {};
    const value = (_b = props.internalData.value) !== null && _b !== void 0 ? _b : data;
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const readOnly = props.config.readOnly || ((_c = props.rootParams.formParams) === null || _c === void 0 ? void 0 : _c.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, error: !!error, disabled: props.config.disabled },
        React.createElement(DateRangePicker, { label: formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label, minDate: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.minDate, maxDate: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.maxDate, disabled: props.config.disabled, readOnly: readOnly, required: (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.required, value: value, fullWidth: true, error: !!error, tabIndex: props.editMode ? -1 : undefined, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                props.setData({
                    from: newValue.from,
                    to: newValue.to,
                });
                props.internalData.value = newValue;
                renderForm(props, { changed: true, autoSaveImmediately: true });
            }, autoFocus: (_h = (!props.editMode && props.config.autoFocus)) !== null && _h !== void 0 ? _h : false, ariaDescribedBy: ariaDescribedBy }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormDateRangeEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Date Range Picker: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
        }), buildValidateTab(props, {
            required: true,
            children: [
                {
                    type: 'form.datePicker',
                    key: 'validate.minDate',
                    label: 'Min Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.minDateMessage',
                    label: 'Min Date Message',
                    emptyAsNull: true,
                },
                {
                    type: 'form.datePicker',
                    key: 'validate.maxDate',
                    label: 'Max Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.maxDateMessage',
                    label: 'Max Date Message',
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDateRangePicker, Object.assign({}, props))));
};
export const widgetFormDateRangePicker = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const { from, to } = (_a = props.getData()) !== null && _a !== void 0 ? _a : {};
        let errorMessage;
        const isValidDateRange = () => {
            var _a;
            if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && (!from || !to)) {
                return false;
            }
            if (from && !isValid(parseISO(from))) {
                return false;
            }
            return !(to && !isValid(parseISO(to)));
        };
        const getErrorMessage = () => {
            var _a, _b;
            if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && (!from || !to)) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
            return 'Invalid date range';
        };
        if ((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.minDate) {
            const minDate = parseISO(props.config.validate.minDate);
            if (isValid(minDate) && from && parseISO(from) < minDate) {
                errorMessage = (_c = props.config.validate.minDateMessage) !== null && _c !== void 0 ? _c : 'Start date cannot be before ' + minDate.toLocaleDateString();
            }
        }
        if ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.maxDate) {
            const maxDate = parseISO(props.config.validate.maxDate);
            if (isValid(maxDate) && to && parseISO(to) > maxDate) {
                errorMessage = (_e = props.config.validate.maxDateMessage) !== null && _e !== void 0 ? _e : 'End date cannot be after ' + maxDate.toLocaleDateString();
            }
        }
        if (from && to && parseISO(from) > parseISO(to)) {
            errorMessage = 'End date cannot be earlier than start date';
        }
        const customError = validateCustom(props, { from, to });
        if (customError) {
            errorMessage = customError;
        }
        if (((_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required) && (!from || !to)) {
            errorMessage = (_g = props.config.validate.requiredMessage) !== null && _g !== void 0 ? _g : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return errorMessage !== null && errorMessage !== void 0 ? errorMessage : (!isValidDateRange() ? getErrorMessage() : undefined);
    },
    update: handleWidgetUpdate,
    component: WidgetFormDateRangeEditor,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDateRangeEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'dateRangePicker', label: 'Date Range Picker' }),
};
